import React from "react";
import {
  type Booking,
  useScheduleData,
  getPharmacistAvatarProps,
} from "../scheduleData";
import { getPatientConsultationByConsultationId } from "../../../dispatchers/patientDispatchers";

interface BookingCardProps {
  booking: Booking;
  serviceColors: Record<string, string>;
  serviceColorsFromData?: Record<string, string>;
  onEdit: (bookingId: string, bookingData: any) => void;
  onDelete: (bookingId: string) => void;
  pharmacistNames: string[];
  rooms: string[];
  size?: "small" | "medium" | "large";
  isReadOnly?: boolean;
  isDarkMode?: boolean;
}

export function BookingCard({
  booking,
  serviceColors,
  serviceColorsFromData = {},
  onEdit,
  onDelete,
  pharmacistNames,
  rooms,
  size = "medium",
  isReadOnly = false,
  isDarkMode = false,
}: BookingCardProps) {
  const { pharmacists, currentPharmacyId } = useScheduleData();

  const isOtherPharmacy =
    Boolean(booking.otherPharmacyId) && String(booking.otherPharmacyId) !== String(currentPharmacyId);

  // Find the pharmacist object for avatar/initials
  let avatarProps = null;
  if (
    booking.pharmacist &&
    booking.pharmacist.toLowerCase() !== "unknown pharmacist"
  ) {
    const pharmacistProfile = pharmacists.find(
      (p) => p.name === booking.pharmacist,
    );
    avatarProps = pharmacistProfile
      ? getPharmacistAvatarProps(
          pharmacistProfile,
          size === "small" ? "sm" : size === "medium" ? "md" : "lg",
        )
      : null;
  }

  const handleBookingClick = async () => {
    if (isReadOnly) return;
    await getPatientConsultationByConsultationId(booking?.consultationID);
    onEdit(booking.id, booking);
  };

  const avatarSize =
    size === "small" ? "w-5 h-5" : size === "medium" ? "w-6 h-6" : "w-10 h-10";
  const deleteButtonSize =
    size === "small" ? "h-3 w-3" : size === "medium" ? "h-4 w-4" : "h-6 w-6";
  const deleteIconSize =
    size === "small" ? "w-2 h-2" : size === "medium" ? "w-2 h-2" : "w-3 h-3";

  // Get color from service data first, then hex color from booking, then fallback to service type mapping
  const bookingColor =
    booking.serviceColor ||
    serviceColorsFromData[booking.serviceName] ||
    serviceColors[booking.serviceType] ||
    "#6b7280"; // default gray hex
  const isCancelledBooking = booking.eventStatus === 4;
  const cancelledSuffix = isCancelledBooking ? " (Cancelled)" : "";

  const isHexColor = bookingColor.startsWith("#");

  // Use the same service color for all bookings, including other pharmacy events
  const bgStyle =
    isCancelledBooking || !isHexColor
      ? {}
      : {
          backgroundColor: bookingColor,
          borderColor: isHexColor ? bookingColor : undefined,
          borderWidth: "0px",
        };

  const colorClassName = isCancelledBooking
    ? "bg-red-600 text-white border border-red-600"
    : isHexColor
      ? "text-white shadow-sm"
      : bookingColor;

  if (size === "large") {
    return (
      <>
        <div
          className="group relative pointer-events-auto"
          onClick={(e) => e.stopPropagation()}
        >
          <div
            className={`p-3 rounded-lg ${isReadOnly ? "" : "cursor-pointer hover:opacity-80"} transition-opacity ${colorClassName}`}
            style={bgStyle}
            title={`${booking.patientName} - ${booking.serviceName}${cancelledSuffix} - ${booking.pharmacist} - ${booking.time}`}
            onClick={(e) => {
              e.stopPropagation();
              handleBookingClick();
            }}
          >
            <div className="flex items-center justify-between">
              <div className="flex items-start gap-3 min-w-0 flex-1">
                {avatarProps &&
                  (avatarProps.type === "image" ? (
                    <img
                      src={avatarProps.src}
                      alt={avatarProps.alt}
                      className={`${avatarSize} rounded-full object-cover flex-shrink-0 mt-1 border-2 border-white shadow-sm`}
                    />
                  ) : (
                    <div
                      className={`${avatarSize} rounded-full flex items-center justify-center flex-shrink-0 mt-1 border-2 border-white shadow-sm bg-blue-500 text-white font-medium text-xs`}
                    >
                      {avatarProps.initials}
                    </div>
                  ))}
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 min-w-0">
                    <span className="font-semibold text-sm truncate">
                      {booking.patientName}
                    </span>
                    {booking.otherPharmacyName && (
                      <span className="inline-flex items-center justify-center text-[10px] font-medium px-2.5 py-1 rounded-full border border-slate-300 dark:border-slate-600 bg-slate-50/90 dark:bg-slate-800 text-slate-600 dark:text-slate-300 flex-shrink-0 truncate max-w-[150px] leading-none shadow-xs">
                        {booking.otherPharmacyName}
                      </span>
                    )}
                  </div>
                  <div className="text-xs opacity-90 font-medium">
                    {booking.time}
                    {cancelledSuffix}
                  </div>
                  <div className="text-xs opacity-75 truncate">
                    {booking.serviceName}
                  </div>
                  {booking.notes && (
                    <div className="text-xs font-medium opacity-90 italic truncate">
                      Notes: {booking.notes}
                    </div>
                  )}
                </div>
              </div>
              <div className="text-sm flex-shrink-0 ml-2">{booking.duration} min</div>
            </div>
          </div>
        </div>
      </>
    );
  }

  return (
    <div onClick={(e) => e.stopPropagation()}>
      <div
        className="group relative pointer-events-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div
          className={`${booking.isMyAppointments ? "p-1.5" : "text-xs p-1.5"} rounded ${isReadOnly ? "" : "cursor-pointer hover:opacity-80"} transition-opacity ${colorClassName} flex flex-col gap-0.5 justify-center`}
          style={bgStyle}
          title={`${booking.patientName} - ${booking.serviceName}${cancelledSuffix} - ${booking.pharmacist} - ${booking.time}`}
          onClick={(e) => {
            e.stopPropagation();
            handleBookingClick();
          }}
        >
          {/* Line 1: Patient Name & Pharmacy Label */}
          <div className="flex items-center gap-2 min-w-0">
            <span
              className={`truncate font-semibold ${
                booking.isMyAppointments ? "text-[13px]" : "text-xs"
              }`}
            >
              {booking.patientName}
            </span>
            {isOtherPharmacy && booking.otherPharmacyName && (
              <span className="inline-flex items-center justify-center text-[10px] font-medium px-2.5 py-1 rounded-full border border-slate-300 dark:border-slate-600 bg-slate-50/90 dark:bg-slate-800 text-slate-600 dark:text-slate-300 flex-shrink-0 truncate max-w-[130px] leading-none shadow-xs">
                {booking.otherPharmacyName}
              </span>
            )}
          </div>

          {/* Line 2: Timing with Avatar */}
          <div className="flex items-center gap-1 min-w-0">
            {avatarProps &&
              (avatarProps.type === "image" ? (
                <img
                  src={avatarProps.src}
                  alt={avatarProps.alt}
                  className="w-3.5 h-3.5 rounded-full object-cover flex-shrink-0 border border-white shadow-sm"
                />
              ) : (
                <div
                  className="w-3.5 h-3.5 rounded-full flex items-center justify-center flex-shrink-0 border border-white shadow-sm bg-blue-500 text-white font-medium text-[8px]"
                >
                  {avatarProps.initials}
                </div>
              ))}
            <div className="truncate text-[11px] opacity-90 font-normal">
              {booking.time}
              {cancelledSuffix}
            </div>
          </div>

          {/* Line 3: Service type */}
          <div
            className={`truncate text-[11px] opacity-80 font-normal`}
          >
            {booking.serviceName}
          </div>

          {booking.isMyAppointments && booking.notes && size !== "small" && (
            <div className="truncate opacity-90 text-[10px] italic font-medium leading-tight">
              {booking.notes}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
